import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, Plus, Trash2, ChevronRight, Check } from 'lucide-react';
import useJobDescriptionState, { IJobDescription } from '@/states/job.state';
import useKeyActivityState, { IKeyActivity } from '@/states/key-activities.state';
import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import useTenantState from '@/states/tenant.state';
import useOrgChartState from '@/states/organization.state';
import { ORG_DIVISIONS, ORG_LEVELS } from '@/utils/constants/organization-matrix';
import StepTipsPanel from '@/components/company/StepTipsPanel';

const FREQUENCY_OPTIONS = ['Harian', 'Mingguan', 'Bulanan', 'Tahunan', 'Insidentil'];

interface InlineRow {
  tempId: string;
  existingId: number | null;
  org_key_activity_id: string;
  name: string;
  frequency: string;
  report: string;
}

let tempIdCounter = 0;
const newTempId = () => {
  tempIdCounter += 1;
  return `jd-row-${tempIdCounter}-${Date.now()}`;
};

const emptyRow = (): InlineRow => ({
  tempId: newTempId(),
  existingId: null,
  org_key_activity_id: '',
  name: '',
  frequency: 'Harian',
  report: '',
});

const validateJobDescriptionRows = (
  rowsToProcess: InlineRow[],
): {
  hasErrors: boolean;
  errors: Record<string, { org_key_activity_id: boolean; name: boolean; report: boolean }>;
} => {
  let hasErrors = false;
  const errors: Record<string, { org_key_activity_id: boolean; name: boolean; report: boolean }> = {};
  for (const row of rowsToProcess) {
    const rowErrors = {
      org_key_activity_id: !row.org_key_activity_id,
      name: !row.name.trim(),
      report: !row.report.trim(),
    };
    if (rowErrors.org_key_activity_id || rowErrors.name || rowErrors.report) {
      errors[row.tempId] = rowErrors;
      hasErrors = true;
    }
  }
  return { hasErrors, errors };
};

interface OrgChartNode {
  id: number;
  name?: string;
  division_key?: string;
  level_key?: string;
  children?: OrgChartNode[];
}

function findNodeById(nodes: OrgChartNode[], id: number): OrgChartNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children?.length) {
      const found = findNodeById(n.children, id);
      if (found) return found;
    }
  }
  return null;
}

interface GroupedRowsProps {
  rows: InlineRow[];
  keyActivities: { id: string | number; name?: string }[];
  isReadOnly: boolean;
  validationErrors: Record<string, { org_key_activity_id?: boolean; name?: boolean; report?: boolean }>;
  updateRow: (tempId: string, patch: Partial<InlineRow>) => void;
  removeRow: (tempId: string) => void;
  addRowToGroup: (kaId: string) => void;
}

function GroupedRows({
  rows,
  keyActivities,
  isReadOnly,
  validationErrors,
  updateRow,
  removeRow,
  addRowToGroup,
}: GroupedRowsProps) {
  const groups: { kaId: string; rows: InlineRow[] }[] = [];
  for (const row of rows) {
    const last = groups[groups.length - 1];
    if (last && last.kaId === row.org_key_activity_id && row.org_key_activity_id !== '') {
      last.rows.push(row);
    } else {
      groups.push({ kaId: row.org_key_activity_id, rows: [row] });
    }
  }

  return (
    <>
      {groups.flatMap(group => {
        const rowSpan = !isReadOnly ? group.rows.length + 1 : group.rows.length;

        const dataRows: JSX.Element[] = group.rows.map((row, rowIndex) => (
          <tr
            key={row.tempId}
            className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50/40 dark:hover:bg-gray-800/50"
          >
            {rowIndex === 0 && (
              <td rowSpan={rowSpan} className="px-2 py-2 align-top border-r border-gray-100 dark:border-gray-700">
                <div className="text-xs w-full px-2 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100 rounded-lg font-medium">
                  {keyActivities.find(ka => String(ka.id) === group.kaId)?.name || 'Belum ada key activities'}
                </div>
              </td>
            )}
            <td className="px-2 py-2">
              <input
                type="text"
                value={row.name}
                onChange={e => updateRow(row.tempId, { name: e.target.value })}
                disabled={!row.org_key_activity_id}
                placeholder={row.org_key_activity_id ? 'Masukkan uraian tugas' : 'Pilih Key Activity terlebih dahulu'}
                className={`text-xs w-full h-8 px-2 border rounded-lg focus:outline-none focus:ring-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400 disabled:cursor-not-allowed ${
                  validationErrors[row.tempId]?.name
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                }`}
              />
            </td>
            <td className="px-2 py-2">
              <select
                value={row.frequency}
                onChange={e => updateRow(row.tempId, { frequency: e.target.value })}
                disabled={isReadOnly}
                className="text-xs w-full h-8 px-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500"
              >
                {FREQUENCY_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </td>
            <td className="px-2 py-2">
              <input
                type="text"
                value={row.report}
                onChange={e => updateRow(row.tempId, { report: e.target.value })}
                disabled={isReadOnly}
                placeholder="Nama Laporan Hasil Kerja"
                className={`text-xs w-full h-8 px-2 border rounded-lg focus:outline-none focus:ring-1 disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:text-gray-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                  validationErrors[row.tempId]?.report
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
                }`}
              />
            </td>
            <td className="px-1 py-2">
              <div className="flex items-center justify-center">
                {!isReadOnly && (
                  <button
                    type="button"
                    title="Hapus baris"
                    onClick={() => removeRow(row.tempId)}
                    className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </td>
          </tr>
        ));

        if (!isReadOnly) {
          dataRows.push(
            <tr
              key={`add-row-${group.kaId || 'empty'}-${group.rows.length}`}
              className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30"
            >
              <td colSpan={4} className="px-4 py-1.5">
                <button
                  type="button"
                  onClick={() => addRowToGroup(group.kaId)}
                  className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                >
                  <Plus size={13} />
                  Tambah Uraian Tugas
                </button>
              </td>
            </tr>,
          );
        }

        return dataRows;
      })}
    </>
  );
}

export default function JobDescriptionDetailContent() {
  const navigate = useNavigate();
  const { tenant_id, nodeId } = useParams<{ tenant_id: string; nodeId: string }>();
  const isReadOnly = useIsReadOnly();
  const currentTenant = useTenantState(state => state.currentTenant);
  const { orgCharts, fetchOrgCharts } = useOrgChartState();
  const { keyActivities, getKeyActivities } = useKeyActivityState();
  const { getJobDescriptions, createJobDescription, updateJobDescription, deleteJobDescription, isLoading } =
    useJobDescriptionState();

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const jobDescStep = companyProfileSteps.find(step =>
    ['role', 'job description'].includes(step.step_name.trim().toLowerCase()),
  );
  const videoLink = jobDescStep?.video_link;
  const tips = jobDescStep?.tips;

  const [rows, setRows] = useState<InlineRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showPanel, setShowPanel] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, { org_key_activity_id?: boolean; name?: boolean; report?: boolean }>
  >({});
  const initializedRef = useRef(false);

  const nodeIdNum = nodeId ? Number(nodeId) : undefined;
  const selectedNode = nodeIdNum ? findNodeById(orgCharts as OrgChartNode[], nodeIdNum) : null;
  const peranName = selectedNode?.name ?? '';

  const rawDivisionKey = selectedNode?.division_key;
  const divisionLabel = ORG_DIVISIONS.find(d => d.key === rawDivisionKey)?.label ?? rawDivisionKey ?? '';

  const rawLevelKey = selectedNode?.level_key;
  const levelLabel = ORG_LEVELS.find(l => l.key === rawLevelKey)?.label ?? rawLevelKey ?? '';

  const fetchData = useCallback(async () => {
    if (!currentTenant?.id || !nodeIdNum) return;
    const [, kaResult] = await Promise.all([
      fetchOrgCharts(currentTenant.id),
      getKeyActivities(currentTenant.id, { page: 0, page_size: 200, order: 'asc', order_by: 'created_at' }),
    ]);
    const fetchedKeyActivities = kaResult?.content || [];
    const result = await getJobDescriptions(currentTenant.id, {
      page: 0,
      page_size: 200,
      org_chart_node_id: nodeIdNum,
    });
    if (result && !initializedRef.current) {
      initializedRef.current = true;
      const content = (result as { content: IJobDescription[] }).content ?? [];
      const roleKeyActivities = (fetchedKeyActivities as IKeyActivity[]).filter(
        ka => ka.org_chart_node_id === nodeIdNum,
      );

      // Always build rows from all key activities of this role.
      // For each KA, use its saved JDs if any, otherwise add one empty row.
      const allRows: InlineRow[] = roleKeyActivities.flatMap(ka => {
        const kaJds = content.filter(jd => String(jd.org_key_activity_id) === String(ka.id));
        if (kaJds.length > 0) {
          return kaJds.map((jd: IJobDescription) => ({
            tempId: newTempId(),
            existingId: jd.id != null ? Number(jd.id) : null,
            org_key_activity_id: String(ka.id),
            name: jd.name ?? '',
            frequency: jd.frequency ?? 'Harian',
            report: jd.report ?? '',
          }));
        }
        return [
          {
            tempId: newTempId(),
            existingId: null,
            org_key_activity_id: String(ka.id),
            name: '',
            frequency: 'Harian',
            report: '',
          },
        ];
      });

      setRows(allRows);
    }
  }, [currentTenant?.id, nodeIdNum, fetchOrgCharts, getKeyActivities, getJobDescriptions]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addRowToGroup = (kaId: string) => {
    setIsDirty(true);
    const newRow: InlineRow = { ...emptyRow(), org_key_activity_id: kaId };
    setRows(prev => {
      const lastIdx = prev.map(r => r.org_key_activity_id).lastIndexOf(kaId);
      const next = [...prev];
      next.splice(lastIdx === -1 ? next.length : lastIdx + 1, 0, newRow);
      return next;
    });
  };

  const updateRow = (tempId: string, patch: Partial<InlineRow>) => {
    setIsDirty(true);
    setValidationErrors(prev => {
      if (!prev[tempId]) return prev;
      const newErrors = { ...prev };
      delete newErrors[tempId];
      return newErrors;
    });
    setRows(prev => prev.map(r => (r.tempId === tempId ? { ...r, ...patch } : r)));
  };

  const removeRow = (tempId: string) => {
    setIsDirty(true);
    setRows(prev => prev.filter(r => r.tempId !== tempId));
  };

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedModal(true);
    } else {
      navigate(`/${tenant_id}/job-description`);
    }
  };

  const processSaveRows = async (
    validRows: InlineRow[],
    existingIds: Set<number | null>,
    savedIds: Set<number>,
    tenantId: number,
    nodeIdNum: number,
  ) => {
    for (const row of validRows) {
      const payload: Partial<IJobDescription> = {
        org_chart_node_id: nodeIdNum,
        org_key_activity_id: Number(row.org_key_activity_id),
        name: row.name.trim(),
        frequency: row.frequency,
        report: row.report.trim(),
        is_published: false,
      };

      if (row.existingId && existingIds.has(row.existingId)) {
        await updateJobDescription(tenantId, row.existingId, payload);
        savedIds.add(row.existingId);
      } else {
        await createJobDescription(tenantId, payload);
      }
    }
  };

  const processCleanupDeletedRows = async (
    existing: IJobDescription[],
    savedIds: Set<number>,
    validRows: InlineRow[],
    tenantId: number,
  ) => {
    for (const jd of existing) {
      const jdId = jd.id != null ? Number(jd.id) : null;
      if (jdId && !savedIds.has(jdId) && !validRows.find(r => r.existingId === jdId)) {
        await deleteJobDescription(tenantId, jdId);
      }
    }
  };

  const handleSaveAndNext = async (returnToList?: boolean | React.MouseEvent) => {
    if (!currentTenant?.id || !nodeIdNum) return;

    const rowsToProcess = rows.filter(r => r.org_key_activity_id || r.name.trim() || r.report.trim());

    if (rowsToProcess.length === 0) {
      toast.error('Isi minimal satu uraian tugas dengan aktivitas, nama tugas, dan nama laporan');
      return;
    }

    const { hasErrors, errors } = validateJobDescriptionRows(rowsToProcess);

    if (hasErrors) {
      setValidationErrors(errors);
      toast.error('Mohon lengkapi semua kolom yang wajib diisi');
      return;
    }

    setValidationErrors({});
    const validRows = rowsToProcess;

    setIsSaving(true);
    try {
      const result = await getJobDescriptions(currentTenant.id, {
        page: 0,
        page_size: 200,
        org_chart_node_id: nodeIdNum,
      });
      const existing = (result as { content: IJobDescription[] })?.content ?? [];
      const existingIds = new Set(existing.map(jd => (jd.id != null ? Number(jd.id) : null)));
      const savedIds = new Set<number>();

      await processSaveRows(validRows, existingIds, savedIds, currentTenant.id, nodeIdNum);
      await processCleanupDeletedRows(existing, savedIds, validRows, currentTenant.id);

      toast.success('Job description berhasil disimpan');
      setIsDirty(false);

      const navigatePath = returnToList === true ? `/${tenant_id}/job-description` : `/${tenant_id}/report`;
      navigate(navigatePath);
    } catch {
      toast.error('Gagal menyimpan job description');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="w-full">
      {/* Header */}
      <div className="p-5 flex flex-col md:flex-row md:items-start justify-between gap-3">
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-primary self-start px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ArrowLeft size={14} /> Kembali ke Daftar Jabatan
          </button>
          {peranName && (
            <div>
              <p className="font-bold text-blue-700">{peranName}</p>
              <p className="text-xs text-gray-500">
                {levelLabel}
                {divisionLabel ? ` | Departemen: ${divisionLabel.toUpperCase()}` : ''}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={() => handleSaveAndNext(true)}
              disabled={isSaving}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
              style={{ background: '#FD6118' }}
            >
              <Check size={15} />
              {isSaving ? 'Menyimpan...' : 'Simpan & Kembali'}
            </button>
          )}
        </div>
      </div>

      <div className="mx-5 border-b border-gray-200 dark:border-gray-700" />

      {/* Main content */}
      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          {isLoading && rows.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-10">Memuat data...</p>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
              <div className="overflow-x-auto">
                <table className="min-w-[860px] w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left w-[220px]">
                        AKTIVITAS UTAMA (KEY ACTIVITY) <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left">
                        RINCIAN URAIAN TUGAS <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left w-[140px]">
                        FREKUENSI <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left">
                        NAMA LAPORAN <span className="text-red-400">*</span>
                      </th>
                      <th className="px-3 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-center w-16">
                        AKSI
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <GroupedRows
                      rows={rows}
                      keyActivities={keyActivities}
                      isReadOnly={isReadOnly}
                      validationErrors={validationErrors}
                      updateRow={updateRow}
                      removeRow={removeRow}
                      addRowToGroup={addRowToGroup}
                    />
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {showPanel && (
          <div className="w-full xl:w-80 shrink-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} />
          </div>
        )}
      </div>

      {showUnsavedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-md w-full p-6 space-y-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Perubahan Belum Disimpan</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Anda memiliki perubahan yang belum disimpan. Apakah Anda ingin menyimpan perubahan sebelum kembali?
            </p>
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowUnsavedModal(false)}
                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={() => navigate(`/${tenant_id}/job-description`)}
                className="px-4 py-2 text-sm font-medium border border-red-200 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 dark:bg-red-900/20 dark:border-red-900/30 dark:hover:bg-red-900/40 transition-colors"
              >
                Hapus Perubahan
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowUnsavedModal(false);
                  handleSaveAndNext(true);
                }}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
              >
                {isSaving ? 'Menyimpan...' : 'Simpan dan Kembali'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
